import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart drawer upsell row. Adds the chosen variant with the same flow as Horizon's product form: announce a
 * CartLinesUpdateEvent, post to /cart/add with the drawer's sections, then resolve the event with the cart and the
 * rendered sections so Horizon's cart-items-component refreshes the drawer (which re-renders this row too).
 */
class CartUpsell extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);

    // Stick just above the drawer footer: tell the scroll area how tall the footer is.
    const content = this.closest('.cart-drawer__content');
    const summary = content?.querySelector('.cart-drawer__summary');
    if (content instanceof HTMLElement && summary) {
      this.#observer = new ResizeObserver(() => {
        content.style.setProperty('--cart-summary-height', `${summary.getBoundingClientRect().height}px`);
      });
      this.#observer.observe(summary);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-upsell-form]')) return;
    event.preventDefault();

    const button = form.querySelector('button');
    const id = new FormData(form).get('id');
    if (!id || button?.disabled) return;
    if (button) button.disabled = true;

    const root = window.Shopify?.routes?.root ?? '/';
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    button?.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const added = await response.json();
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-upsell',
          sections: added.sections,
          didError: Boolean(added.status),
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (button) button.disabled = false;
    }
  };
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
